import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import React from 'react';

export interface OptionProps {
    key: string;
    label: string;
    labelIsI18Key?: boolean;
    iconKey?: IconKey;
    disabled?: boolean;
}

interface Props {
    options: OptionProps[];
    title: string | React.ReactNode;
    onItemSelected: (key: string) => void;
    disabled?: boolean;
    menuMaxHeight?: string;
    menuButtonClassName?: string;
    label?: string;
    className?: string;
    anchor?: 'top start' | 'top end' | 'bottom start' | 'bottom end';
    menuItemClassName?: string;
    /** Overrides the dropdown panel's own background/text, e.g. for use on a dark surface. */
    menuItemsClassName?: string;
}

const DropdownMenuComponent = ({
    title,
    options,
    onItemSelected,
    disabled = false,
    menuMaxHeight = '!max-h-72',
    menuButtonClassName,
    label,
    className,
    anchor = 'bottom start',
    menuItemClassName = '',
    menuItemsClassName,
}: Props) => {
    const { i18next } = useAppContext();
    return (
        <Menu
            as="div"
            className={twMerge('relative text-left', className)}
            data-test-id={'dropdown'}
        >
            {label ? (
                <label className="mb-1 block text-sm font-bold text-gray-800">
                    {label}
                </label>
            ) : null}
            <MenuButton
                disabled={disabled}
                className={twMerge(
                    'inline-flex w-full items-center justify-between rounded-sm bg-gray-200 px-3 py-2 text-sm whitespace-nowrap dark:bg-gray-100',
                    menuButtonClassName,
                    disabled
                        ? 'text-gray-300'
                        : 'hover:darken-10 text-gray-800 hover:rounded-sm',
                )}
            >
                <div className="truncate">{title}</div>
                <Icon iconKey="arrow_down" />
            </MenuButton>
            <MenuItems
                anchor={anchor}
                className={twMerge(
                    'scrollbar shadow-base scrollbar-gutter-stable absolute end-0 z-50 mt-2 w-(--button-width) origin-top-right rounded-md bg-white py-2 ps-2 outline-gray-100 focus:outline dark:bg-gray-300',
                    menuMaxHeight,
                    menuItemsClassName,
                )}
            >
                {options.map((option) => (
                    <MenuItem
                        key={`mi-${option.key}`}
                        onClick={() => onItemSelected(option.key)}
                        disabled={disabled}
                        dataTestId={`dropdown-option-${option.key}`}
                        className={menuItemClassName}
                    >
                        {option.iconKey ? (
                            <Icon iconKey={option.iconKey} className="me-2" />
                        ) : null}
                        <span>
                            {option.labelIsI18Key
                                ? i18next.t(option.label)
                                : option.label}
                        </span>
                    </MenuItem>
                ))}
            </MenuItems>
        </Menu>
    );
};

export default DropdownMenuComponent;
